import { defineRule } from 'vee-validate';
import { min_value, max } from '@vee-validate/rules';

export default {
    data() {
        defineRule('is-valid-ledgernumber', (value, { isValidLedgerNumber, rowData }) => {
            if (isValidLedgerNumber(value, rowData)) return true;
            return 'Invalid ledger number conflict with an existing Chart of Account';
        }),
        defineRule('is-valid-type', (value, { isValidType, rowData }) => {
            if (isValidType(value, rowData)) return true;
            return 'Please select a valid chart of accounts type.';
        }),
        defineRule('is-valid-category', (value, { isValidCategory, rowData }) => {
            if (isValidCategory(value, rowData)) return true;
            return 'Please select a valid chart of accounts category.';
        });   

        return {
            chartOfAccountsConfiguration: {
                type: 'ChartOfAccounts',
                label: 'Accounting Chart of Accounts',
                sampleNotes: [
                ],
                importNotes: () => {
                    const hasDuplicates = this.loadedData.some(row => this.isDuplicateLedgerInImport(row));
                    return hasDuplicates
                        ? ['Duplicate Chart of Account entries were found in the import file.  These will not be processed.']
                        : [];
                },
                fields: [
                    {
                        key: 'LedgerNumber',
                        label: 'Ledger Number',
                        hasErrorCalculator: (value, rowData) => {
                            return !this.isValidLedgerNumber(value, rowData);
                        },
                        hasWarningCalculator: (value, row) => {
                            return this.isDuplicateLedgerInImport(row);
                        },
                        rules: {
                            'is-valid-ledgernumber': {
                                isValidLedgerNumber: (value, row) =>
                                    this.isValidLedgerNumber(value, row)
                            },
                            max: { length: 255 }
                        },
                        addRowDataToRule: 'is-valid-ledgernumber',
                        samples: [
                            '"1000"',
                            '"2000"'
                        ]
                    },
                    {
                        key: 'Type',
                        label: 'Type',
                        hasErrorCalculator: (value, rowData) => {
                            return !this.isValidType(value, rowData);
                        },
                        rules: {
                            'is-valid-type': {
                                isValidType: (value, row) =>
                                    this.isValidType(value, row)
                            }
                        },
                        addRowDataToRule: 'is-valid-type',
                        samples: ['"Sales Tax"', '"Pay Out"'],
                        dataType: 'chartOfAccountsTypes'
                    },
                    {
                        key: 'Category',
                        label: 'Category',
                        hasErrorCalculator: (value, rowData) => {
                            return !this.isValidCategory(value, rowData);
                        },
                        rules: {
                            'is-valid-category': {
                                isValidCategory: (value, row) =>
                                    this.isValidCategory(value, row)
                            }
                        },
                        addRowDataToRule: 'is-valid-category',
                        samples: ['"Expenses"', '"Other Income"'],
                        dataType: 'chartOfAccountsCategoryDescriptions'
                    },                     
                    {
                        key: 'Description',
                        label: 'Description',
                        hasErrorCalculator: value => {
                            return max(value, { length: 255 }) !== true;
                        },
                        rules: 'max:255',
                        samples: [
                            '"Note about this record"', 
                            '"Name changed 1/24/2024"'
                        ],
                    },
                    {
                        key: 'IsActive',
                        label: 'Is Active',
                        hasWarningCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['Yes', 'No', ''],
                        dataType: 'yesNoOptions',
                        defaultValue: 'Yes'
                    },                                       
                ],
                canRunNoParse: true,
                cronJobTypeId: 1
            }
        };
    },
    methods: {
        isNewChartOfAccount(row) {
            // The select-list payload uses `text` for the composite display string
            // (e.g. "1000 - INC|Income - Credit - Revenue") and exposes the raw
            // ledger number on `glCode`. Match on `glCode`, not `text`.
            var ledger = row.LedgerNumber?.toLowerCase();
            var chartOfAccount = this.selectListOptions.chartOfAccounts.find(account => account.glCode?.toLowerCase() === ledger);

            return {
                isNew: chartOfAccount == undefined,
                chartOfAccountId: chartOfAccount?.value
            }
        },
        isDuplicateChartOfAccount(chartOfAccountId, row) {
            var ledger = row.LedgerNumber?.toLowerCase();
            for (let option of this.selectListOptions.chartOfAccounts) {
                if(chartOfAccountId !== option.value && ledger === option.glCode?.toLowerCase()) {
                    return true;
                }
            }

            return false;
        },
        isDuplicateLedgerInImport(row) {
            const ledgerNumber = row.LedgerNumber?.toLowerCase();
            if (!ledgerNumber) return false;
            const matches = this.loadedData.filter(x => x.LedgerNumber?.toLowerCase() === ledgerNumber);
            // The first occurrence passes as valid; only subsequent duplicates warn.
            return matches.length > 1 && matches[0] !== row;
        },
        isValidLedgerNumber(value, row) {
            const isEmpty = !value;
            const { isNew, chartOfAccountId } = this.isNewChartOfAccount(row);
            const isValidLength = max(value, { length: 255 }) === true;

            // Required
            if (isEmpty) return false;

            // Reject ledger numbers that already exist in the system
            if (!isNew) return false;

            // Reject duplicates within the imported file
            if (this.isDuplicateChartOfAccount(chartOfAccountId, row)) return false;

            return isValidLength;
        },
        isValidType(value, row) {
            const isEmpty = value === '' || value === undefined;
            const { isNew } = this.isNewChartOfAccount(row);
            const isValid = min_value(value, { min: 0 }) === true;

            return isNew
                ? !isEmpty && isValid
                : isEmpty || isValid;
        },
        isValidCategory(value, row) {
            const isEmpty = value === '' || value === undefined;
            const { isNew } = this.isNewChartOfAccount(row);
            const isValid = min_value(value, { min: 0 }) === true;

            return isNew
                ? !isEmpty && isValid
                : isEmpty || isValid;
        }
    }
};
